import { SCENARIOS } from "../config/campaign";
import { TOWERS } from "../config/towers";
import { MAPS } from "../config/map";
import type {
  DifficultyId,
  LevelResult,
  PlayerProgress,
  SaveData,
  TowerDefinition,
} from "../domain/types";

const STARTER_TOWERS = ["archer", "rocket", "lightning", "cannon"] as const;
const DIFFICULTY_RANK: Readonly<Record<DifficultyId, number>> = {
  easy: 0,
  medium: 1,
  hard: 2,
};
const DEFAULT_STATISTICS = {
  victories: 0,
  defeats: 0,
  bossesDefeated: 0,
  highestWave: 0,
  totalScore: 0,
} as const;

export interface ProgressionAward {
  readonly scenarioId: string;
  readonly firstCompletion: boolean;
  readonly starsAwarded: number;
  readonly royalSealsAwarded: number;
  readonly newlyUnlockedScenarioIds: readonly string[];
  readonly newlyUnlockedTowerIds: readonly string[];
  readonly save: SaveData;
}

export interface ProgressionDiagnostics {
  readonly completedScenarioIds: readonly string[];
  readonly invalidCompletionIds: readonly string[];
  readonly totalStars: number;
  readonly royalSeals: number;
  readonly unlockedScenarioIds: readonly string[];
  readonly nextPlayableScenarioIds: readonly string[];
  readonly unlockedTowerIds: readonly string[];
  readonly firstScenarioUnlocksSecond: boolean;
}

export function totalStars(progress: PlayerProgress): number {
  return Object.values(progress.scenarioStars ?? {}).reduce((sum, value) => sum + value, 0);
}

export function isScenarioUnlocked(scenarioId: string, progress: PlayerProgress): boolean {
  const scenario = SCENARIOS.find((entry) => entry.id === scenarioId);
  if (!scenario) return false;
  if (scenario.unlockRequirement === "default") return true;

  const mapOrder = Object.values(MAPS).map((map) => map.id);
  const mapIndex = mapOrder.indexOf(scenario.mapId);
  if (mapIndex === 0) return true;
  if (mapIndex < 0) return false;

  const previousMapId = mapOrder[mapIndex - 1];
  const requiredRank = DIFFICULTY_RANK[scenario.difficulty];

  return SCENARIOS.some(
    (candidate) =>
      candidate.mapId === previousMapId &&
      DIFFICULTY_RANK[candidate.difficulty] >= requiredRank &&
      progress.completedLevels.includes(candidate.id),
  );
}

export function canonicalScenarioId(
  value: string | undefined,
  fallbackMapId?: string,
  fallbackDifficulty: DifficultyId = "easy",
): string | null {
  if (value && SCENARIOS.some((scenario) => scenario.id === value)) return value;
  const mapId = value && MAPS[value] ? value : fallbackMapId;
  if (!mapId || !MAPS[mapId]) return null;
  const requested = `${mapId}:${fallbackDifficulty}`;
  return SCENARIOS.some((scenario) => scenario.id === requested) ? requested : `${mapId}:easy`;
}

export function isTowerUnlocked(tower: TowerDefinition, progress: PlayerProgress): boolean {
  if (STARTER_TOWERS.includes(tower.id as (typeof STARTER_TOWERS)[number])) return true;
  if (progress.unlockedTowers.includes(tower.id)) return true;
  const requirement = tower.unlockRequirement;
  if (requirement === "default") return true;
  if (requirement.endsWith("-stars")) {
    const needed = Number.parseInt(requirement, 10);
    return Number.isFinite(needed) && totalStars(progress) >= needed;
  }
  return progress.completedLevels.includes(requirement);
}

export function deriveUnlockedTowerIds(progress: PlayerProgress): readonly string[] {
  return Object.values(TOWERS)
    .filter((tower) => isTowerUnlocked(tower, progress))
    .map((tower) => tower.id);
}

function betterResult(candidate: LevelResult, previous: LevelResult | undefined): boolean {
  if (!previous) return true;
  if (candidate.victory !== previous.victory) return candidate.victory;
  if (candidate.wavesCompleted !== previous.wavesCompleted)
    return candidate.wavesCompleted > previous.wavesCompleted;
  if (candidate.score !== previous.score) return candidate.score > previous.score;
  return candidate.livesRemaining > previous.livesRemaining;
}

function normalizeResult(
  value: Partial<LevelResult>,
  key?: string,
): { scenarioId: string; result: LevelResult } | null {
  const scenarioId =
    canonicalScenarioId(value.scenarioId, value.mapId, value.difficulty) ??
    canonicalScenarioId(key, value.mapId, value.difficulty) ??
    canonicalScenarioId(undefined, value.mapId, value.difficulty);
  if (!scenarioId) return null;
  const scenario = SCENARIOS.find((entry) => entry.id === scenarioId);
  if (!scenario) return null;
  return {
    scenarioId,
    result: {
      mapId: scenario.mapId,
      scenarioId,
      difficulty: scenario.difficulty,
      startingLives:
        Number.isFinite(value.startingLives) && Number(value.startingLives) > 0
          ? Number(value.startingLives)
          : scenario.startingLives,
      victory: value.victory === true,
      wavesCompleted: Math.max(0, Number(value.wavesCompleted) || 0),
      livesRemaining: Math.max(0, Number(value.livesRemaining) || 0),
      score: Math.max(0, Number(value.score) || 0),
      completedAt:
        typeof value.completedAt === "string" ? value.completedAt : new Date(0).toISOString(),
    },
  };
}

function achievementIds(progress: PlayerProgress, latestResult?: LevelResult): readonly string[] {
  const achievements = new Set(progress.achievements ?? []);
  if ((progress.statistics?.victories ?? 0) > 0) achievements.add("first-victory");
  if (latestResult?.victory && latestResult.livesRemaining >= latestResult.startingLives)
    achievements.add("flawless-defense");
  if (latestResult?.victory && latestResult.difficulty === "hard")
    achievements.add("hard-won-crown");
  if ((progress.statistics?.highestWave ?? 0) >= 50) achievements.add("fifty-waves");
  return [...achievements];
}

/**
 * Repairs the scenario-key mismatch used by older builds. Legacy map completions
 * become easy-scenario completions, victorious best results restore missing
 * completion records, and scenario rewards are restored to a deterministic floor.
 */
export function repairPlayerProgress(progress: PlayerProgress): PlayerProgress {
  const completed = new Set<string>();
  for (const id of progress.completedLevels ?? []) {
    const canonical = canonicalScenarioId(id);
    if (canonical) completed.add(canonical);
  }

  const bestResults: Record<string, LevelResult> = {};
  for (const [key, rawResult] of Object.entries(progress.bestResults ?? {})) {
    const normalized = normalizeResult(rawResult as Partial<LevelResult>, key);
    if (!normalized) continue;
    if (betterResult(normalized.result, bestResults[normalized.scenarioId]))
      bestResults[normalized.scenarioId] = normalized.result;
    if (normalized.result.victory) completed.add(normalized.scenarioId);
  }

  const scenarioStars: Record<string, number> = {};
  for (const [key, stars] of Object.entries(progress.scenarioStars ?? {})) {
    const canonical = canonicalScenarioId(key);
    if (!canonical) continue;
    scenarioStars[canonical] = Math.max(
      scenarioStars[canonical] ?? 0,
      PhaserSafeNumber(stars, 0, 3),
    );
  }
  for (const scenarioId of completed) {
    const scenario = SCENARIOS.find((entry) => entry.id === scenarioId);
    if (scenario)
      scenarioStars[scenarioId] = Math.max(scenarioStars[scenarioId] ?? 0, scenario.rewards.stars);
  }

  const orderedCompleted = SCENARIOS.map((scenario) => scenario.id).filter((id) =>
    completed.has(id),
  );
  const rewardFloor = orderedCompleted.reduce((sum, scenarioId) => {
    const scenario = SCENARIOS.find((entry) => entry.id === scenarioId);
    return sum + (scenario?.rewards.royalSeals ?? 0);
  }, 0);
  const statistics = {
    ...DEFAULT_STATISTICS,
    ...progress.statistics,
    victories: Math.max(progress.statistics?.victories ?? 0, orderedCompleted.length),
  };
  const lastScenarioId =
    canonicalScenarioId(progress.lastScenarioId) ??
    orderedCompleted.at(-1) ??
    "castle-approach:easy";
  const repairedBase: PlayerProgress = {
    ...progress,
    completedLevels: orderedCompleted,
    bestResults,
    progressionCurrency: Math.max(progress.progressionCurrency ?? 0, rewardFloor),
    royalSeals: Math.max(progress.royalSeals ?? 0, rewardFloor),
    scenarioStars,
    namedLoadouts: progress.namedLoadouts ?? {},
    lastScenarioId,
    achievements: progress.achievements ?? [],
    statistics,
    unlockedTowers: [...new Set(progress.unlockedTowers ?? STARTER_TOWERS)],
  };
  return {
    ...repairedBase,
    achievements: achievementIds(repairedBase),
    unlockedTowers: deriveUnlockedTowerIds(repairedBase),
  };
}

function PhaserSafeNumber(value: unknown, minimum: number, maximum: number): number {
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? Math.max(minimum, Math.min(maximum, Math.floor(numeric)))
    : minimum;
}

export function applyLevelResult(save: SaveData, rawResult: LevelResult): ProgressionAward {
  const repaired = repairPlayerProgress(save.progress);
  const normalized = normalizeResult(rawResult);
  if (!normalized) {
    return {
      scenarioId: rawResult.scenarioId,
      firstCompletion: false,
      starsAwarded: 0,
      royalSealsAwarded: 0,
      newlyUnlockedScenarioIds: [],
      newlyUnlockedTowerIds: [],
      save: { ...save, progress: repaired },
    };
  }
  const { scenarioId, result } = normalized;
  const scenario = SCENARIOS.find((entry) => entry.id === scenarioId)!;
  const beforeScenarios = new Set(
    SCENARIOS.filter((entry) => isScenarioUnlocked(entry.id, repaired)).map((entry) => entry.id),
  );
  const beforeTowers = new Set(deriveUnlockedTowerIds(repaired));
  const firstCompletion = result.victory && !repaired.completedLevels.includes(scenarioId);
  const completedLevels = result.victory
    ? [...new Set([...repaired.completedLevels, scenarioId])]
    : [...repaired.completedLevels];
  const previousBest = repaired.bestResults[scenarioId];
  const bestResults = {
    ...repaired.bestResults,
    ...(betterResult(result, previousBest) ? { [scenarioId]: result } : {}),
  };
  const starsAwarded = firstCompletion ? scenario.rewards.stars : 0;
  const royalSealsAwarded = firstCompletion ? scenario.rewards.royalSeals : 0;
  const scenarioStars = {
    ...(repaired.scenarioStars ?? {}),
    ...(result.victory
      ? {
          [scenarioId]: Math.max(repaired.scenarioStars?.[scenarioId] ?? 0, scenario.rewards.stars),
        }
      : {}),
  };
  const previousStatistics = repaired.statistics ?? DEFAULT_STATISTICS;
  const statistics = {
    ...previousStatistics,
    victories: previousStatistics.victories + (result.victory ? 1 : 0),
    defeats: previousStatistics.defeats + (result.victory ? 0 : 1),
    highestWave: Math.max(previousStatistics.highestWave, result.wavesCompleted),
    totalScore: previousStatistics.totalScore + result.score,
  };
  const progressBase: PlayerProgress = {
    ...repaired,
    completedLevels,
    bestResults,
    progressionCurrency: repaired.progressionCurrency + royalSealsAwarded,
    royalSeals: (repaired.royalSeals ?? 0) + royalSealsAwarded,
    scenarioStars,
    lastScenarioId: scenarioId,
    statistics,
  };
  const progress: PlayerProgress = {
    ...progressBase,
    achievements: achievementIds(progressBase, result),
    unlockedTowers: deriveUnlockedTowerIds(progressBase),
  };
  const afterScenarios = SCENARIOS.filter((entry) => isScenarioUnlocked(entry.id, progress)).map(
    (entry) => entry.id,
  );
  const afterTowers = deriveUnlockedTowerIds(progress);
  return {
    scenarioId,
    firstCompletion,
    starsAwarded,
    royalSealsAwarded,
    newlyUnlockedScenarioIds: afterScenarios.filter((id) => !beforeScenarios.has(id)),
    newlyUnlockedTowerIds: afterTowers.filter((id) => !beforeTowers.has(id)),
    save: { ...save, progress: { ...progress, unlockedTowers: afterTowers } },
  };
}

export function describeScenarioRequirement(scenarioId: string, progress: PlayerProgress): string {
  const scenario = SCENARIOS.find((entry) => entry.id === scenarioId);
  if (!scenario) return "Unknown campaign scenario";
  if (isScenarioUnlocked(scenarioId, progress)) return "Available";
  const mapIndex = Object.values(MAPS).findIndex((map) => map.id === scenario.mapId);
  if (mapIndex <= 0) return "Available";
  const label =
    scenario.difficulty === "easy"
      ? "Previous map: Easy+"
      : scenario.difficulty === "medium"
        ? "Previous map: Medium+"
        : "Previous map: Hard";
  return label;
}

export function progressionDiagnostics(progress: PlayerProgress): ProgressionDiagnostics {
  const repaired = repairPlayerProgress(progress);
  const validScenarioIds = new Set(SCENARIOS.map((scenario) => scenario.id));
  const invalidCompletionIds = (progress.completedLevels ?? []).filter(
    (id) => !validScenarioIds.has(id) && !MAPS[id],
  );
  const unlockedScenarioIds = SCENARIOS.filter((scenario) =>
    isScenarioUnlocked(scenario.id, repaired),
  ).map((scenario) => scenario.id);
  const nextPlayableScenarioIds = unlockedScenarioIds.filter(
    (id) => !repaired.completedLevels.includes(id),
  );
  const firstCompletion: PlayerProgress = {
    ...repaired,
    completedLevels: ["castle-approach:easy"],
  };
  return {
    completedScenarioIds: repaired.completedLevels,
    invalidCompletionIds,
    totalStars: totalStars(repaired),
    royalSeals: repaired.royalSeals ?? 0,
    unlockedScenarioIds,
    nextPlayableScenarioIds,
    unlockedTowerIds: deriveUnlockedTowerIds(repaired),
    firstScenarioUnlocksSecond: isScenarioUnlocked("royal-courtyard:easy", firstCompletion),
  };
}

export function describeTowerRequirement(tower: TowerDefinition): string {
  const requirement = tower.unlockRequirement;
  if (requirement === "default") return "Available";
  if (requirement.endsWith("-stars"))
    return `Earn ${Number.parseInt(requirement, 10)} campaign stars`;
  const scenario = SCENARIOS.find((entry) => entry.id === requirement);
  return scenario
    ? `Complete ${scenario.displayName} on ${scenario.mapId.replaceAll("-", " ")}`
    : requirement;
}

export function earnedStars(
  victory: boolean,
  livesRemaining: number,
  startingLives: number,
): number {
  if (!victory) return 0;
  if (livesRemaining >= startingLives) return 3;
  if (livesRemaining >= Math.ceil(startingLives / 2)) return 2;
  return 1;
}
